/**
 * Participant Payments Hook
 * 
 * React hook for managing participant payment data and operations
 */

import { useState, useEffect, useCallback } from 'react';
import { 
  participantPaymentService, 
  ParticipantPayment, 
  ParticipantPaymentDetails,
  ParticipantPaymentFilters 
} from '../services/participantPaymentService';
import { toast } from '@etherisc/ui-kit';

export function useParticipantPayments(initialFilters: ParticipantPaymentFilters = {}) {
  const [payments, setPayments] = useState<ParticipantPayment[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 25,
    total: 0,
    totalPages: 0
  });
  const [filters, setFilters] = useState<ParticipantPaymentFilters>(initialFilters);

  const fetchPayments = useCallback(async (newFilters?: ParticipantPaymentFilters) => {
    try {
      setLoading(true);
      setError(null);
      
      const filtersToUse = newFilters || filters;
      const response = await participantPaymentService.listParticipantPayments(filtersToUse);
      
      setPayments(response.payments);
      setPagination(response.pagination);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load participant payments';
      setError(errorMessage);
      toast.error('Failed to load payments', { description: errorMessage });
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const refreshPayments = useCallback(() => {
    fetchPayments();
  }, [fetchPayments]);

  const updateFilters = useCallback((newFilters: Partial<ParticipantPaymentFilters>) => {
    const updatedFilters = { ...filters, ...newFilters };
    setFilters(updatedFilters);
    fetchPayments(updatedFilters);
  }, [filters, fetchPayments]);

  const changePage = useCallback((page: number) => {
    updateFilters({ page });
  }, [updateFilters]);

  const changeLimit = useCallback((limit: number) => {
    updateFilters({ page: 1, limit });
  }, [updateFilters]);

  // Load initial data
  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);

  return {
    payments,
    loading,
    error,
    pagination,
    filters,
    fetchPayments,
    refreshPayments,
    updateFilters,
    changePage,
    changeLimit
  };
}

export function useParticipantPaymentDetails(paymentId?: string) {
  const [paymentDetails, setPaymentDetails] = useState<ParticipantPaymentDetails | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchPaymentDetails = useCallback(async (id: string) => {
    try {
      setLoading(true);
      setError(null);
      
      const details = await participantPaymentService.getParticipantPayment(id);
      setPaymentDetails(details);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load payment details';
      setError(errorMessage);
      toast.error('Failed to load payment details', { description: errorMessage });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (paymentId) {
      fetchPaymentDetails(paymentId);
    }
  }, [paymentId, fetchPaymentDetails]);

  return {
    paymentDetails,
    loading,
    error,
    fetchPaymentDetails
  };
}

export function useRetryPayment() {
  const [loading, setLoading] = useState(false);

  const retryPayment = useCallback(async (paymentId: string): Promise<boolean> => {
    try {
      setLoading(true);
      
      const result = await participantPaymentService.retryPayment(paymentId);
      
      toast.success('Payment retry successful', { 
        description: `Authorization ID: ${result.authorizationId}` 
      });
      
      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Payment retry failed';
      toast.error('Payment retry failed', { description: errorMessage });
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    retryPayment,
    loading
  };
}
